'use client';

import { LngLat, SuggestResponseItem } from '@yandex/ymaps3-types';
import { clusterByGrid as clusterByGridMethod } from '@yandex/ymaps3-types/packages/clusterer';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';

import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';
import { UiKitPanelBase, UiKitPanelBaseProps } from '@/fo/ui/kit/panel/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { useMaps } from '@/fo/ui/mod/yandex/maps';

import { MapContent } from '../components';
import { PickupPointInfo } from '../components/PickupPointInfo/PickupPointInfo';
import { PickupPointSelector } from '../components/PickupPointSelector/PickupPointSelector';
import { SearchLocationField } from '../components/SearchLocationField/SearchLocationField';
import { DEFAULT_CLUSTER_GRID_SIZE } from '../consts';
import { getAddressFromSuggest } from '../helpers/getAddressFromSuggest';
import { getStorePoints } from '../helpers/getPointsByTypes';
import { useMapController, usePointsController } from '../hooks';
import { PickupPoint, PointFeature, StorePoint } from '../types';

import { styles } from './UiPickupMapsBase.styles';

interface StoreProps {
  points: StorePoint[];
  variant: 'store';
}

interface DefaultProps {
  points: PickupPoint[];
  variant?: 'default';
}

export type UiPickupMapsBaseProps = (StoreProps | DefaultProps) & {
  onPointConfirm?: (pointId: string) => void;
  city?: string;
  /**
   * Смещение поля поиска относительно правого края экрана
   * Может использоваться для диалоговых окон где справа будет кнопка закрытия
   * Указывается в пикселях
   */
  searchFieldRightOffset?: number;
  /**
   * Флаг для отображения только карты на мобильных устройствах
   */
  isMobileOnlyMap?: boolean;
  /**
   * Заголовок для селектора точек
   */
  pointSelectorTitle?: string;
  /**
   * Флаг для отображения селектора точек, по умолчанию true
   */
  isHasPointTypeSelector?: boolean;

  center?: LngLat;

  hideSearchField?: boolean;

  pickupPointSelectorSize?: 'small' | 'default';

  tabsView?: UiKitPanelBaseProps['view'];

  loading?: boolean;

  isFreeShipping?: boolean;
};

export function UiPickupMapsBase({
  onPointConfirm,
  city,
  searchFieldRightOffset,
  isMobileOnlyMap = false,
  pointSelectorTitle,
  isHasPointTypeSelector = true,
  center,
  hideSearchField = false,
  pickupPointSelectorSize = 'default',
  tabsView = 'tab',
  loading = false,
  isFreeShipping,
  ...props
}: UiPickupMapsBaseProps) {
  const slots = styles();

  const t = useTranslations();

  const { reactifyApi, clusterer, suggest, search } = useMaps();

  const isDefaultVariant = !props.variant || props.variant === 'default';

  const {
    location,
    selectedPoint,
    handleMarkerClick,
    handleClusterClick,
    handleLocationSelect
  } = useMapController(center);

  const {
    visiblePoints,
    selectedPointType,
    handlePointTypeChange,
    pointTypes
  } = usePointsController(isDefaultVariant ? props.points : []);

  const pointsList = isDefaultVariant
    ? visiblePoints.flatMap(({ points }) => points)
    : getStorePoints(props.points);

  const [openDrawer, setOpenDrawer] = useState(false);

  const isDesktop = useMediaQuery('lg');

  const gridSizedMethod = useMemo(() => {
    if (!clusterer) {
      return null;
    }

    const clusterByGrid =
      clusterer.clusterByGrid as unknown as typeof clusterByGridMethod;

    return clusterByGrid({ gridSize: DEFAULT_CLUSTER_GRID_SIZE });
  }, [clusterer]);

  const handleSuggestSelect = async (option: SuggestResponseItem | null) => {
    if (!search || !option) {
      return;
    }

    const result = await search({
      text: getAddressFromSuggest(option)
    });

    const firstResult = result?.[0];

    const coordinates = firstResult?.geometry?.coordinates;

    if (coordinates) {
      handleLocationSelect(coordinates as [number, number]);
    }
  };

  const handlePointConfirm = (pointId: string) => {
    onPointConfirm?.(pointId);
  };

  const onMapMarkerClickWithDrawer = useCallback(
    (feature: PointFeature) => {
      const result = handleMarkerClick(true)(feature);

      if (!isDesktop && result.isMap) {
        setOpenDrawer(true);
      }
    },
    [handleMarkerClick, isDesktop, setOpenDrawer]
  );

  if (!reactifyApi || !clusterer || !gridSizedMethod || !suggest) {
    return null;
  }

  const pickupPointSelector = (
    <PickupPointSelector
      size={pickupPointSelectorSize}
      key="pickup-point-selector"
      onPointConfirm={handlePointConfirm}
      selectedPointType={selectedPointType}
      activePointId={selectedPoint?.id ?? null}
      pointsList={pointsList}
      pointTypes={pointTypes}
      onSelectPoint={handleMarkerClick(false)}
      onPointTypeChange={handlePointTypeChange}
      pointSelectorTitle={pointSelectorTitle}
      isHasPointTypeSelector={isHasPointTypeSelector}
      hideConfirmButton={!onPointConfirm}
      loading={loading}
      isFreeShipping={isFreeShipping}
    />
  );

  const mapNode = (
    <MapContent
      key="map-content"
      reactifyApi={reactifyApi}
      clusterer={clusterer}
      gridSizedMethod={gridSizedMethod}
      pointsList={pointsList}
      selectedPoint={selectedPoint}
      location={location}
      isDesktop={isDesktop}
      onMarkerClick={onMapMarkerClickWithDrawer}
      onClusterClick={handleClusterClick}
    />
  );

  const searchLocationField = hideSearchField ? null : (
    <div
      className={slots.searchLocationField({ mobileView: !isDesktop })}
      style={
        {
          ...(!!searchFieldRightOffset && {
            '--pickup-additional-offset': `${searchFieldRightOffset}px`
          })
        } as React.CSSProperties
      }
    >
      <SearchLocationField
        suggest={suggest}
        onSelect={handleSuggestSelect}
        city={city}
      />
    </div>
  );

  if (isDesktop) {
    return (
      <div className={slots.base()}>
        {isDesktop && (
          <div
            className={slots.pickupPointSelector({
              size: pickupPointSelectorSize
            })}
          >
            {pickupPointSelector}
          </div>
        )}
        {searchLocationField}
        {mapNode}
      </div>
    );
  }

  const mobileMapNode = (
    <div className={slots.panelContent()}>
      {searchLocationField}
      {mapNode}
    </div>
  );

  return (
    <>
      <UiKitDrawerBase
        title=""
        open={openDrawer && !!selectedPoint}
        onOpenChange={setOpenDrawer}
        direction="bottom"
      >
        {selectedPoint && (
          <PickupPointInfo
            point={selectedPoint}
            activePointId={selectedPoint?.id ?? null}
            onPointConfirm={handlePointConfirm}
            hideConfirmButton={!onPointConfirm}
            isFreeShipping={isFreeShipping}
          />
        )}
      </UiKitDrawerBase>
      {isMobileOnlyMap ? (
        <div className={slots.panelContent()}>{mobileMapNode}</div>
      ) : (
        <UiKitPanelBase
          tabsClassName={slots.tabs()}
          tabClassName={slots.tab()}
          view={tabsView}
          items={[
            {
              header: t('asMap'),
              content: (
                <div className={slots.panelContent()}>{mobileMapNode}</div>
              )
            },
            {
              header: t('asList'),
              content: (
                <div className={slots.panelContent()}>
                  {pickupPointSelector}
                </div>
              )
            }
          ]}
        />
      )}
    </>
  );
}
